import { Check, Landmark, Wallet, FileText } from "lucide-react";

const CARD = { background: "#141416", border: "0.5px solid #26262b", borderRadius: 12 };

function Cell({ on, onToggle, disabled, color, testId }) {
  return (
    <button type="button" disabled={disabled} onClick={onToggle} className="inline-flex items-center justify-center" aria-pressed={on} data-testid={testId}
      style={{ width: 20, height: 20, borderRadius: 5, border: `0.5px solid ${on ? color : "#3a3a42"}`, background: on ? color : "transparent", opacity: disabled ? .45 : 1, cursor: disabled ? "not-allowed" : "pointer" }}>
      {on && <Check size={12} style={{ color: "#fff" }} />}
    </button>
  );
}

/**
 * Grille partagée « ressource × action » (comptes Trésorerie, types de documents).
 * `value` = { resourceId: [actions] } ; toute action implique la première (« Voir »).
 */
export default function ResourceRightsSection({ title, icon: Icon, color, resources, actions, actionLabel, value, onChange, disabled, groups, emptyText, testId }) {
  const v = value || {};
  const view = actions[0];
  const has = (rid, a) => (v[rid] || []).includes(a);
  const toggle = (rid, a) => {
    let acts = new Set(v[rid] || []);
    if (acts.has(a)) { acts.delete(a); if (a === view) acts = new Set(); } else { acts.add(a); acts.add(view); }
    onChange({ ...v, [rid]: actions.filter((x) => acts.has(x)) });
  };
  const toggleRow = (rid) => onChange({ ...v, [rid]: (v[rid] || []).length === actions.length ? [] : [...actions] });
  const allFull = resources.length > 0 && resources.every((r) => (v[r.id] || []).length === actions.length);
  const toggleAll = () => onChange(Object.fromEntries(resources.map((r) => [r.id, allFull ? [] : [...actions]])));
  const granted = resources.filter((r) => (v[r.id] || []).length).length;
  const grouped = groups ? groups.map(([g, GIcon, pred]) => [g, GIcon, resources.filter(pred)]).filter(([, , xs]) => xs.length) : [[null, null, resources]];
  return (
    <div style={{ ...CARD, padding: "8px 6px" }} data-testid={testId}>
      <div className="flex items-center justify-between" style={{ padding: "0 8px 6px" }}>
        <span className="inline-flex items-center" style={{ gap: 6, fontSize: 10, letterSpacing: ".08em", textTransform: "uppercase", color: "#8a8a8f", fontWeight: 700 }}>
          {Icon && <Icon size={12} style={{ color }} />}{title} <span style={{ color: "#5a5a63", fontWeight: 500 }} data-testid={`${testId}-count`}>{granted}/{resources.length}</span>
        </span>
        {resources.length > 0 && <button type="button" disabled={disabled} onClick={toggleAll} style={{ fontSize: 10.5, padding: "2px 8px", borderRadius: 6, border: "0.5px solid #3a3a42", color: allFull ? "#E0A42E" : "#c9c9d1", background: "#18181c" }} data-testid={`${testId}-all`}>{allFull ? "Aucun" : "Tout"}</button>}
      </div>
      {resources.length === 0 && <div style={{ fontSize: 12, color: "#6e6e78", padding: "4px 8px" }}>{emptyText}</div>}
      {grouped.map(([g, GIcon, xs]) => (
        <div key={g || "all"} style={{ padding: "0 2px" }}>
          {g && <div className="flex items-center" style={{ gap: 6, fontSize: 10.5, color: "#c8c8cf", padding: "6px 6px 2px" }}>{GIcon && <GIcon size={12} style={{ color }} />}{g}</div>}
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={{ textAlign: "left", fontSize: 10, color: "#8a8a92", padding: "4px 6px", fontWeight: 500 }}>{g ? "" : "Ressource"}</th>{actions.map((a) => <th key={a} style={{ fontSize: 10, color: "#8a8a92", padding: "4px 4px", fontWeight: 500, textAlign: "center" }}>{actionLabel[a] || a}</th>)}<th style={{ fontSize: 10, color: "#8a8a92", padding: "4px 4px", fontWeight: 500 }}>Tout</th></tr></thead>
            <tbody>{xs.map((r) => (
              <tr key={r.id} style={{ borderTop: "0.5px solid #1e1e22" }} data-testid={`${testId}-row-${r.id}`}>
                <td style={{ padding: "6px 6px" }}>
                  <span className="block truncate" style={{ fontSize: 12, color: "#e8e8ec" }}>{r.label}</span>
                  {r.sub && <span className="block truncate" style={{ fontSize: 10, color: "#8a8a92" }}>{r.sub}</span>}
                </td>
                {actions.map((a) => <td key={a} style={{ textAlign: "center", padding: "4px" }}><Cell on={has(r.id, a)} disabled={disabled} color={color} onToggle={() => toggle(r.id, a)} testId={`${testId}-${r.id}-${a}`} /></td>)}
                <td style={{ textAlign: "center", padding: "4px" }}><button type="button" disabled={disabled} onClick={() => toggleRow(r.id)} style={{ fontSize: 10.5, color, opacity: disabled ? .4 : 1 }} data-testid={`${testId}-${r.id}-all`}>{(v[r.id] || []).length === actions.length ? "Aucun" : "Tous"}</button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      ))}
      <div style={{ fontSize: 10.5, color: "#6e6e78", padding: "6px 8px 0" }}>Toute action implique « {actionLabel[view] || view} ».</div>
    </div>
  );
}

export const TRESO_ACTION_LABEL = { voir: "Voir", encaisser: "Encaisser", decaisser: "Décaisser", transferer: "Transférer", modifier: "Modifier", supprimer: "Supprimer", configurer: "Configurer" };
export const TRESO_GROUPS = [["Comptes bancaires", Landmark, (a) => a.nature === "bancaire"], ["Caisses", Wallet, (a) => a.nature !== "bancaire"]];
export const DOCTYPE_ACTION_LABEL = { view: "Voir", create: "Créer", edit: "Modifier", advance: "Faire avancer" };
export const DOCTYPE_ACTIONS = ["view", "create", "edit", "advance"];
export const DocTypeIcon = FileText;
